/** @format */

import {
  LeftOutlined,
  LockOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Alert, Button, DatePicker, Input, Segmented, Select, Spin } from "antd";
import moment from "moment";
import React, { memo, useMemo, useState } from "react";
import { TModalitaPresenze } from "../../../../redux-modules/declarations/maps/turniSettimana";
import {
  TConteggiPresenze,
  TGruppoPresenze,
  TPersonaGiornata,
  TPresenzeGiornata,
  TRepartoPresenze,
} from "../../../../redux-modules/declarations/maps/presenzeVista";
import { L } from "../../../../theme/maps/StatiPresenze";
import {
  chipSegnalazione,
  contestiDelGiorno,
  corrispondeAlFiltro,
  corrispondeNome,
  dataEstesaMaiuscola,
  descrizioneSegnalazione,
  FASCE,
  formatDurata,
  nomeCompleto,
  ora,
  plurale,
  testoStatoPersona,
  TFascia,
  TFiltroGiornata,
  tinta,
  TUnitaPresenze,
} from "../format";
import { oreAsse, TFasciaMinuti } from "../linea";
import { BarraStrumenti, GrigliaGiornata, LegendaPresenze, Settimana, Stato } from "../styles";
import { TLettura } from "../usePresenze";
import { AvatarPersona } from "./AvatarPersona";
import { LineaGiorno } from "./LineaGiorno";

export interface TPreferenzeGiornata {
  unita: TUnitaPresenze;
  fascia: TFascia;
  gruppi: string[];
  reparti: string[];
  chiusi: string[];
}

interface Props {
  lettura: TLettura<TPresenzeGiornata>;
  data: string;
  preferenze: TPreferenzeGiornata;
  giustificativi: { nome: string; colore: string | null }[];
  onData: (data: string) => void;
  onPreferenze: (modifica: Partial<TPreferenzeGiornata>) => void;
  onApriPersona: (persona: TPersonaGiornata) => void;
}

export const chiaveId = (id: number | null): string => (id === null ? "nessuno" : String(id));

const VUOTI: TConteggiPresenze = {
  persone: 0,
  in_servizio: 0,
  usciti: 0,
  assenti: 0,
  non_timbrati: 0,
  da_iniziare: 0,
  da_gestire: 0,
};

/** Somma dei conteggi del backend (per reparto): nessun conteggio ricalcolato dal web. */
export const sommaConteggi = (elenco: TConteggiPresenze[]): TConteggiPresenze =>
  elenco.reduce(
    (tot, c) => ({
      persone: tot.persone + c.persone,
      in_servizio: tot.in_servizio + c.in_servizio,
      usciti: tot.usciti + c.usciti,
      assenti: tot.assenti + c.assenti,
      non_timbrati: tot.non_timbrati + c.non_timbrati,
      da_iniziare: tot.da_iniziare + c.da_iniziare,
      da_gestire: tot.da_gestire + c.da_gestire,
    }),
    VUOTI,
  );

const FILTRI: {
  valore: TFiltroGiornata;
  etichetta: string;
  conteggio: keyof TConteggiPresenze;
}[] = [
  { valore: "tutti", etichetta: "Tutti", conteggio: "persone" },
  { valore: "in_servizio", etichetta: "In servizio", conteggio: "in_servizio" },
  { valore: "da_iniziare", etichetta: "Da iniziare", conteggio: "da_iniziare" },
  { valore: "usciti", etichetta: "Usciti", conteggio: "usciti" },
  { valore: "non_timbrati", etichetta: "Non timbrati", conteggio: "non_timbrati" },
  { valore: "assenti", etichetta: "Assenti e riposi", conteggio: "assenti" },
  { valore: "da_gestire", etichetta: "Da gestire", conteggio: "da_gestire" },
];

interface TRepartoVista {
  reparto: TRepartoPresenze;
  persone: TPersonaGiornata[];
}

interface TGruppoVista {
  chiave: string;
  gruppo: TGruppoPresenze;
  aperto: boolean;
  conteggi: TConteggiPresenze;
  visibili: number;
  reparti: TRepartoVista[];
}

/** Larghezza stimata della linea nella griglia, per accorciare le etichette dei blocchi. */
const PIXEL_LINEA = 820;

export const Giornata = ({
  lettura,
  data,
  preferenze,
  giustificativi,
  onData,
  onPreferenze,
  onApriPersona,
}: Props): JSX.Element => {
  const { dati, caricamento, aggiornamento, errore, ricarica } = lettura;
  const [ricerca, setRicerca] = useState("");
  const [filtro, setFiltro] = useState<TFiltroGiornata>("tutti");

  const fascia = FASCE[preferenze.fascia];
  const oggi = dati?.giornata.oggi || moment().format("YYYY-MM-DD");
  const adesso = dati && dati.giornata.data === data ? dati.giornata.adesso : null;

  const opzioniGruppi = useMemo(
    () => (dati ? dati.gruppi.map(g => ({ value: chiaveId(g.id), label: g.nome })) : []),
    [dati],
  );
  const opzioniReparti = useMemo(() => {
    if (!dati) return [];
    const visti: Record<string, string> = {};
    dati.gruppi.forEach(g =>
      g.reparti.forEach(r => {
        visti[chiaveId(r.id)] = r.nome;
      }),
    );
    return Object.keys(visti)
      .map(k => ({ value: k, label: visti[k] }))
      .sort((a, b) => a.label.localeCompare(b.label, "it"));
  }, [dati]);

  // filtri salvati che non esistono più non devono svuotare la vista
  const gruppiSel = useMemo(
    () => preferenze.gruppi.filter(g => opzioniGruppi.some(o => o.value === g)),
    [preferenze.gruppi, opzioniGruppi],
  );
  const repartiSel = useMemo(
    () => preferenze.reparti.filter(r => opzioniReparti.some(o => o.value === r)),
    [preferenze.reparti, opzioniReparti],
  );
  const filtrando = !!ricerca.trim() || filtro !== "tutti";

  const vista = useMemo(() => {
    if (!dati) return { gruppi: [] as TGruppoVista[], conteggi: VUOTI };
    const perId: Record<number, TPersonaGiornata> = {};
    dati.persone.forEach(p => {
      perId[p.id] = p;
    });
    const nelPerimetro = (g: TGruppoPresenze, r: TRepartoPresenze): boolean =>
      (gruppiSel.length === 0 || gruppiSel.indexOf(chiaveId(g.id)) >= 0) &&
      (repartiSel.length === 0 || repartiSel.indexOf(chiaveId(r.id)) >= 0);
    const visibile = (p: TPersonaGiornata): boolean =>
      corrispondeAlFiltro(p.giorno, filtro) && corrispondeNome(p, ricerca);

    const gruppi: TGruppoVista[] = [];
    const conteggiPerimetro: TConteggiPresenze[] = [];
    dati.gruppi.forEach(gruppo => {
      const reparti = gruppo.reparti.filter(r => nelPerimetro(gruppo, r));
      if (!reparti.length) return;
      const conteggi = sommaConteggi(reparti.map(r => r.conteggi));
      conteggiPerimetro.push(conteggi);
      const chiave = chiaveId(gruppo.id);
      const repartiVista = reparti.map(reparto => ({
        reparto,
        persone: reparto.persone
          .map(id => perId[id])
          .filter((p): p is TPersonaGiornata => !!p && visibile(p)),
      }));
      gruppi.push({
        chiave,
        gruppo,
        // con una ricerca o un filtro i gruppi si aprono, per mostrare i risultati
        aperto: filtrando || preferenze.chiusi.indexOf(chiave) < 0,
        conteggi,
        visibili: repartiVista.reduce((n, r) => n + r.persone.length, 0),
        reparti: repartiVista,
      });
    });
    const tutto = gruppiSel.length === 0 && repartiSel.length === 0;
    return { gruppi, conteggi: tutto ? dati.conteggi : sommaConteggi(conteggiPerimetro) };
  }, [dati, gruppiSel, repartiSel, filtro, ricerca, filtrando, preferenze.chiusi]);

  const apriChiudi = (chiave: string): void =>
    onPreferenze({
      chiusi:
        preferenze.chiusi.indexOf(chiave) >= 0
          ? preferenze.chiusi.filter(c => c !== chiave)
          : [...preferenze.chiusi, chiave],
    });

  const azzera = (): void => {
    setRicerca("");
    setFiltro("tutti");
  };

  const m = moment(data);
  const nessuno = vista.gruppi.every(g => g.visibili === 0);
  const mostraGruppi = vista.gruppi.filter(g => g.visibili > 0 || !filtrando);

  return (
    <>
      <BarraStrumenti>
        <div className="riga">
          <div className="settimana">
            <Button
              icon={<LeftOutlined />}
              aria-label="Giorno precedente"
              onClick={(): void => onData(m.clone().subtract(1, "day").format("YYYY-MM-DD"))}
            />
            <DatePicker
              allowClear={false}
              value={m}
              onChange={(v): void => {
                if (v) onData(v.format("YYYY-MM-DD"));
              }}
              format={(): string => dataEstesaMaiuscola(data)}
              className="etichetta-settimana"
              style={{ minWidth: 236 }}
              aria-label="Giorno"
            />
            <Button
              icon={<RightOutlined />}
              aria-label="Giorno successivo"
              onClick={(): void => onData(m.clone().add(1, "day").format("YYYY-MM-DD"))}
            />
            <Button onClick={(): void => onData(moment().format("YYYY-MM-DD"))}>Oggi</Button>
          </div>
          <Select
            className="filtro"
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="Tutti i gruppi"
            aria-label="Gruppi"
            value={gruppiSel}
            options={opzioniGruppi}
            optionFilterProp="label"
            onChange={(v: string[]): void => onPreferenze({ gruppi: v })}
          />
          <Select
            className="filtro"
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="Tutti i reparti"
            aria-label="Reparti"
            value={repartiSel}
            options={opzioniReparti}
            optionFilterProp="label"
            onChange={(v: string[]): void => onPreferenze({ reparti: v })}
          />
          <Input
            className="ricerca"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cerca dipendente"
            aria-label="Cerca dipendente"
            value={ricerca}
            onChange={(e): void => setRicerca(e.target.value)}
          />
          <div className="spazio" />
          <Segmented
            aria-label="Fascia oraria"
            value={preferenze.fascia}
            options={[
              { label: "07–21", value: "lavoro" },
              { label: "24 ore", value: "giorno" },
            ]}
            onChange={(v): void => onPreferenze({ fascia: v as TFascia })}
          />
          <Segmented
            aria-label="Unità delle ore"
            value={preferenze.unita}
            options={[
              { label: "Ore e minuti", value: "ore" },
              { label: "Centesimi", value: "centesimi" },
            ]}
            onChange={(v): void => onPreferenze({ unita: v as TUnitaPresenze })}
          />
        </div>
        <div className="riga">
          <span className="mostra">Mostra</span>
          <Segmented
            value={filtro}
            onChange={(v): void => setFiltro(v as TFiltroGiornata)}
            options={FILTRI.map(f => ({
              value: f.valore,
              label: (
                <span>
                  {f.etichetta}
                  <span className={`conteggio${f.valore === "da_gestire" ? " rosso" : ""}`}>
                    {vista.conteggi[f.conteggio]}
                  </span>
                </span>
              ),
            }))}
          />
          <div className="spazio" />
          {dati?.giornata.bloccato && (
            <Stato>
              <LockOutlined /> Giorno bloccato: si può solo consultare
            </Stato>
          )}
          {aggiornamento && <Spin size="small" />}
          {dati && data === oggi && (
            <Stato>Aggiornato alle {ora(dati.giornata.adesso)}, si aggiorna ogni minuto</Stato>
          )}
        </div>
      </BarraStrumenti>

      {errore && (
        <Alert
          type="error"
          showIcon
          message={errore}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={ricarica}>
              Riprova
            </Button>
          }
        />
      )}

      {dati && dati.giornata.data === data && (
        <Settimana aria-label="Settimana">
          {dati.settimana.map(g => {
            let sotto = "Nessuna anomalia";
            let colore = "";
            if (g.futuro) sotto = "Da svolgere";
            else if (g.persone_con_anomalie > 0) {
              sotto = `${plurale(g.persone_con_anomalie, "persona", "persone")} da gestire`;
              colore = " rosso";
            }
            if (g.data === oggi) sotto = `Oggi, ${sotto.charAt(0).toLowerCase()}${sotto.slice(1)}`;
            return (
              <button
                key={g.data}
                type="button"
                aria-pressed={g.data === data}
                title={g.futuro ? undefined : plurale(g.anomalie, "anomalia", "anomalie")}
                onClick={(): void => onData(g.data)}
              >
                <span className="alto">
                  {moment(g.data).format("ddd")} <b>{moment(g.data).format("D")}</b>
                </span>
                <span className={`sotto${colore}`}>{sotto}</span>
              </button>
            );
          })}
        </Settimana>
      )}

      <Spin spinning={caricamento}>
        {dati ? (
          <GrigliaGiornata>
            <div className="contenuto">
              <div className="riga testata">
                <div className="col-nome">Persona e stato</div>
                <div className="asse" aria-hidden="true">
                  {oreAsse(fascia, fascia.passo).map((h, i, tutte) => (
                    <span
                      key={h.etichetta}
                      className={`ora-asse${
                        i === 0 ? " prima" : i === tutte.length - 1 ? " ultima" : ""
                      }`}
                      style={{ left: `${h.sinistra}%` }}
                    >
                      {h.etichetta}
                    </span>
                  ))}
                  {adesso && adesso.substr(0, 10) === data && (
                    <PosizioneAdesso adesso={adesso} fascia={fascia} />
                  )}
                </div>
                <div className="col-cella">Ore lavorate di previste</div>
                <div className="col-cella">Da gestire</div>
              </div>

              {nessuno && (
                <div className="vuoto">
                  <strong>
                    {filtrando
                      ? "Nessuna persona corrisponde alla ricerca con il filtro scelto."
                      : "Nessuna persona ha turni, timbrature o giustificativi in questo giorno."}
                  </strong>
                  {filtrando && (
                    <>
                      <span className="attenuato">
                        La ricerca guarda nome e cognome in qualunque ordine, senza maiuscole e
                        accenti.
                      </span>
                      <Button onClick={azzera}>Mostra tutte le persone</Button>
                    </>
                  )}
                </div>
              )}

              {mostraGruppi.map(g => (
                <React.Fragment key={g.chiave}>
                  <div className="riga gruppo">
                    <div className="col-nome">
                      <button
                        type="button"
                        className="apri-gruppo"
                        aria-expanded={g.aperto}
                        onClick={(): void => apriChiudi(g.chiave)}
                      >
                        <RightOutlined className={`freccia${g.aperto ? " aperta" : ""}`} />
                        {g.gruppo.nome}
                      </button>
                      <span className="conta-persone">
                        {g.visibili === g.conteggi.persone
                          ? plurale(g.conteggi.persone, "persona", "persone")
                          : `${g.visibili} di ${plurale(g.conteggi.persone, "persona", "persone")}`}
                      </span>
                    </div>
                    <div className="centro-gruppo">
                      <BandierineGruppo conteggi={g.conteggi} onFiltro={setFiltro} />
                    </div>
                  </div>
                  {g.aperto &&
                    g.reparti
                      .filter(r => r.persone.length > 0)
                      .map(({ reparto, persone }) => (
                        <React.Fragment key={`${g.chiave}-${chiaveId(reparto.id)}`}>
                          {g.reparti.length > 1 && (
                            <div className="riga reparto">
                              <div className="col-nome">
                                <span className="nome-reparto">{reparto.nome}</span>
                                <span className="conta-persone">
                                  {plurale(reparto.conteggi.persone, "persona", "persone")}
                                </span>
                              </div>
                              <div className="centro-gruppo">
                                <BandierineGruppo
                                  conteggi={reparto.conteggi}
                                  onFiltro={setFiltro}
                                />
                              </div>
                            </div>
                          )}
                          {persone.map(p => (
                            <RigaPersona
                              key={p.id}
                              persona={p}
                              fascia={fascia}
                              adesso={adesso}
                              unita={preferenze.unita}
                              modalita={dati.modalita_presenze}
                              onApri={onApriPersona}
                            />
                          ))}
                        </React.Fragment>
                      ))}
                </React.Fragment>
              ))}
            </div>
          </GrigliaGiornata>
        ) : (
          <div style={{ minHeight: 320 }} />
        )}
      </Spin>

      <Legenda giustificativi={giustificativi} />
    </>
  );
};

const PosizioneAdesso = ({
  adesso,
  fascia,
}: {
  adesso: string;
  fascia: TFasciaMinuti;
}): JSX.Element | null => {
  const minuti = +adesso.substr(11, 2) * 60 + +adesso.substr(14, 2);
  if (minuti < fascia.da || minuti > fascia.a) return null;
  return (
    <span
      className="etichetta-adesso"
      style={{ left: `${((minuti - fascia.da) / (fascia.a - fascia.da)) * 100}%` }}
    >
      {ora(adesso)}
    </span>
  );
};

const BandierineGruppo = ({
  conteggi,
  onFiltro,
}: {
  conteggi: TConteggiPresenze;
  onFiltro: (filtro: TFiltroGiornata) => void;
}): JSX.Element => (
  <>
    <span className="bandierina verde">{conteggi.in_servizio} in servizio</span>
    {conteggi.da_iniziare > 0 && (
      <span className="bandierina grigio">{conteggi.da_iniziare} da iniziare</span>
    )}
    {conteggi.usciti > 0 && (
      <span className="bandierina grigio">{plurale(conteggi.usciti, "uscito", "usciti")}</span>
    )}
    {conteggi.assenti > 0 && (
      <span className="bandierina grigio">
        {plurale(conteggi.assenti, "assente o a riposo", "assenti o a riposo")}
      </span>
    )}
    {conteggi.non_timbrati > 0 && (
      <button
        type="button"
        className="bandierina rosso"
        onClick={(): void => onFiltro("non_timbrati")}
      >
        {plurale(conteggi.non_timbrati, "non timbrato", "non timbrati")}
      </button>
    )}
    {conteggi.da_gestire > 0 && (
      <button
        type="button"
        className="bandierina rosso"
        onClick={(): void => onFiltro("da_gestire")}
      >
        {conteggi.da_gestire} da gestire
      </button>
    )}
  </>
);

interface PropsRiga {
  persona: TPersonaGiornata;
  fascia: TFasciaMinuti;
  adesso: string | null;
  unita: TUnitaPresenze;
  modalita: TModalitaPresenze;
  onApri: (persona: TPersonaGiornata) => void;
}

/** Riga di una persona: memorizzata, si ridisegna solo se cambiano i suoi dati o la vista. */
const RigaPersona = memo(function RigaPersona({
  persona,
  fascia,
  adesso,
  unita,
  modalita,
  onApri,
}: PropsRiga): JSX.Element {
  const g = persona.giorno;
  const stato = testoStatoPersona(g);
  const nome = nomeCompleto(persona);
  const apri = (): void => onApri(persona);
  const contesti = contestiDelGiorno(persona, g);
  const chip = contesti.slice(0, 2);
  const altri = contesti.length - chip.length;
  const assente = g.stato.codice === "assente" || g.stato.codice === "riposo";
  const percentuale =
    g.totali.minuti_previsti > 0
      ? Math.min(100, Math.round((g.totali.minuti_lavorati / g.totali.minuti_previsti) * 100))
      : 0;

  return (
    <div className="riga persona">
      <div className="col-nome nome-persona">
        <button
          type="button"
          className="apri-persona"
          onClick={apri}
          aria-label={`Apri la giornata di ${nome}`}
        >
          <AvatarPersona persona={persona} />
          <span className="nome-stato">
            <span className="nome">{nome}</span>
            <span className={`stato ${stato.classe}`}>{stato.testo}</span>
          </span>
        </button>
        {persona.contratto.single_badge && (
          <span className="etichetta" title="Timbratura singola: una sola timbratura al giorno">
            Singola
          </span>
        )}
      </div>
      <LineaGiorno
        giorno={g}
        fascia={fascia}
        adesso={adesso}
        pixel={PIXEL_LINEA}
        onApri={apri}
        nomePersona={nome}
      />
      <div className="col-ore">
        {assente && g.totali.minuti_lavorati === 0 ? (
          <div className="ore">
            <strong>{"—"}</strong>
            <span>{g.stato.codice === "riposo" ? "riposo" : "assente"}</span>
          </div>
        ) : (
          <>
            <div className="ore">
              <strong>{formatDurata(g.totali.minuti_lavorati, unita)}</strong>
              <span>
                {g.totali.minuti_previsti > 0
                  ? `di ${formatDurata(g.totali.minuti_previsti, unita)}`
                  : "senza turno"}
              </span>
            </div>
            {g.totali.minuti_previsti > 0 && (
              <div className="barra" aria-hidden="true">
                <span
                  className={percentuale >= 100 ? "piena" : undefined}
                  style={{ width: `${percentuale}%` }}
                />
              </div>
            )}
          </>
        )}
      </div>
      <div className="col-gestire">
        {chip.map(c => (
          <button
            key={c.chiave}
            type="button"
            className={`chip ${c.livello === "anomalia" ? "rosso" : "ambra"}`}
            onClick={apri}
            aria-label={chipSegnalazione(c, modalita, unita)}
            title={descrizioneSegnalazione(c, unita)}
          >
            {chipSegnalazione(c, modalita, unita)}
          </button>
        ))}
        {altri > 0 && (
          <button
            type="button"
            className="chip grigio"
            onClick={apri}
            aria-label={`Altre ${altri} segnalazioni`}
          >
            +{altri}
          </button>
        )}
        {!contesti.length && !assente && <span className="niente">Niente da gestire</span>}
      </div>
    </div>
  );
});

const Legenda = ({
  giustificativi,
}: {
  giustificativi: { nome: string; colore: string | null }[];
}): JSX.Element => (
  <LegendaPresenze aria-label="Legenda">
    <div className="riga-legenda">
      <span className="titolo">Stati</span>
      <div className="voci">
        <span className="voce">
          <i
            className="tratteggio"
            style={{ background: L.turno.fondo, borderColor: L.turno.bordo }}
          />
          Turno previsto
        </span>
        <span className="voce">
          <i style={{ background: L.aperta.fondo, borderColor: L.aperta.bordo }} />
          Timbratura in corso
        </span>
        <span className="voce">
          <i style={{ background: L.chiusa.fondo, borderColor: L.chiusa.bordo }} />
          Timbratura chiusa
        </span>
        <span className="voce">
          <i style={{ background: L.straordinario.fondo, borderColor: L.straordinario.bordo }} />
          Straordinario
        </span>
        <span className="voce">
          <i
            className="tratteggio"
            style={{ background: L.nonTimbrato.fondo, borderColor: L.nonTimbrato.bordo }}
          />
          Turno non timbrato
        </span>
        <span className="voce">
          <i style={{ background: L.anomalia.fondo, borderColor: L.anomalia.bordo }} />
          Anomalia
        </span>
        <span className="voce">
          <i style={{ background: L.avviso.fondo, borderColor: L.avviso.bordo }} />
          Avviso
        </span>
        <span className="voce">
          <i
            className="tratteggio righe"
            style={{ backgroundColor: L.inAttesa.fondo, borderColor: L.inAttesa.bordo }}
          />
          Richiesta in attesa
        </span>
        <span className="voce">
          <i style={{ background: L.riposo.fondo, borderColor: L.riposo.bordo }} />
          Riposo
        </span>
        <span className="voce">
          <i style={{ background: L.festa.fondo, borderColor: L.festa.bordo }} />
          Festività
        </span>
      </div>
    </div>
    {giustificativi.length > 0 && (
      <div className="riga-legenda">
        <span className="titolo">Giustificativi</span>
        <div className="voci">
          {giustificativi.map(g => (
            <span key={g.nome} className="voce">
              <i
                className="righe"
                style={{
                  backgroundColor: tinta(g.colore, 0.45),
                  borderColor: tinta(g.colore, 0.9),
                }}
              />
              {g.nome}
            </span>
          ))}
        </div>
      </div>
    )}
  </LegendaPresenze>
);
